import { useCallback, useEffect } from "react";
import {
	useClinicContext,
	useUserContext,
	useAlertContext
} from "../../contexts";
import {
	BuildUrl,
	formatCentreName,
	isTestEnvironment
} from "../../helpers";
import { Clinic, AlertType, ClinicSetting } from "../../models";
import { useMsal } from "@azure/msal-react";
import {
	ADMIN,
	CENTRE_ADMIN,
	EXECUTIVE_CENTRE_ADMIN,
	apiAccessRequest,
	SETTING_QUEUE_ENABLE_TELEHEALTH
} from "../../constants";

export const isOurSage = (name: string): boolean => {
	return name?.toLowerCase().indexOf("our sage") >= 0;
};

export const isTestClinic = (name: string): boolean => {
	return (
		name?.toLowerCase().endsWith(" test") ||
		name?.toLowerCase().endsWith(" development")
	);
};

export const GetClinics = () => {
	const {
		clinics,
		setClinics,
		setTelehealthClinics,
		setUserClinics,
		refreshClinics,
		setRefreshClinics
	} = useClinicContext();
	const { instance } = useMsal();
	const { user, account } = useUserContext();
	const { showAlert } = useAlertContext();

	useEffect(() => {
		if (!clinics || refreshClinics) {
			getClinics();
			setRefreshClinics(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clinics, refreshClinics]);

	const getClinics = useCallback(async () => {
		try {
			const requestUrl = BuildUrl("Clinic/GetClinics");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const response = await fetch(requestUrl, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${tokenResponse.accessToken}`,
					"Content-Type": "application/json"
				},
				body: JSON.stringify({ includeSettings: true })
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const contentLength = response.headers.get("Content-Length");
			if (response.status !== 204 && contentLength !== "0") {
				const result = await response.json();
				const clinics = result
					.filter(
						(clinic: Clinic) =>
							!isOurSage(clinic.name) &&
							(isTestEnvironment() || !isTestClinic(clinic.name))
					)
					.map((clinic: Clinic) => ({
						...clinic,
						name: formatCentreName(clinic.name)
					}));
				setClinics(clinics);

				const telehealthClinics =
					clinics?.filter((clinic: Clinic) =>
						clinic.clinicSettings?.find(
							(setting: ClinicSetting) =>
								setting.settingName === SETTING_QUEUE_ENABLE_TELEHEALTH &&
								setting.value === "1"
						)
					) || [];
				setTelehealthClinics(telehealthClinics);

				if (
					user?.roles.includes(ADMIN) ||
					user?.roles.includes(CENTRE_ADMIN) ||
					user?.roles.includes(EXECUTIVE_CENTRE_ADMIN)
				) {
					setUserClinics(clinics);
					return;
				}
				const centreNames =
					user?.roles
						.filter((role) => role.indexOf("centre_admin_") > -1)
						.map((role) =>
							role.substring(role.lastIndexOf("_") + 1).toLowerCase()
						) ?? [];

				const userClinics = clinics.filter(
					(clinic: Clinic) =>
						centreNames.indexOf(
							clinic.name
								.toLowerCase()
								.replace(/our medical/gi, "")
								.replace(/ /gi, "")
						) >= 0
				);
				setUserClinics(userClinics);
			}
		} catch (error) {
			console.info(error);
			showAlert({
				message: "Error fetching clinics",
				alertType: AlertType.error
			});
		} finally {
			setRefreshClinics(false);
		}
	}, [
		account,
		instance,
		setClinics,
		setRefreshClinics,
		setTelehealthClinics,
		setUserClinics,
		showAlert,
		user?.roles
	]);

	return <></>;
};
